import { Box } from "@mui/material";
import { useId, type CSSProperties, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import {
  SITE_COLOR,
  SITE_EASE,
  SITE_TYPE,
  SITE_VAR,
  SITE_WRAP,
} from "src/domains/shared/constants/styles/site";
import { revealRef } from "../siteKit/motion/reveal";
import { RevealWords } from "../siteKit/RevealWords";
import { SiteSection } from "../siteKit/SiteSection";

const INDEX_STYLES = {
  head: { marginBottom: SITE_VAR.head, textAlign: "center" },
  grid: {
    display: "grid",
    gridTemplateColumns: "repeat(4,minmax(0,1fr))",
    gap: "clamp(32px,4vw,56px) clamp(24px,3vw,44px)",
    "@media (max-width:900px)": {
      gridTemplateColumns: "repeat(2,minmax(0,1fr))",
    },
    "@media (max-width:560px)": { gridTemplateColumns: "1fr", gap: "20px" },
  },
  link: {
    display: "flex",
    flexDirection: "column",
    gap: "10px",
    paddingTop: "24px",
    borderTop: `1px solid ${SITE_COLOR.line}`,
    "& svg[data-icon]": {
      width: "64px",
      height: "48px",
      display: "block",
      marginBottom: "6px",
    },
    "& b": {
      display: "flex",
      alignItems: "center",
      gap: "4px",
      fontSize: "clamp(19px,1.7vw,22px)",
      fontWeight: 700,
      letterSpacing: "-.02em",
      color: SITE_COLOR.ink,
    },
    "& b svg": {
      color: SITE_COLOR.ink3,
      transition: `transform .25s ${SITE_EASE.out},color .2s`,
    },
    "& span": { fontSize: "16px", lineHeight: 1.6, color: SITE_COLOR.ink2 },
    "&:hover b svg": { transform: "translateX(4px)", color: SITE_COLOR.blue },
    "@media (max-width:560px)": { paddingTop: "16px" },
  },
} as const;

/** 기능 색인 그림(시안 그대로 — 등각 판 위 기능 표식) */
const INDEX_ICONS: readonly ReactNode[] = [
  <>
    <path d="M8 26l24-12 24 12-24 12z" fill="#DCE6F2" />
    <path d="M8 26v6l24 12v-6z" fill="#B4C3D7" />
    <path d="M56 26v6L32 44v-6z" fill="#A3B4CB" />
    <path
      d="M14 17l18-9 18 9"
      fill="none"
      stroke="#1D74FF"
      strokeWidth="2.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M20 10l12-6 12 6"
      fill="none"
      stroke="#1D74FF"
      strokeWidth="2.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      opacity=".45"
    />
  </>,
  <>
    <path d="M6 24l20-10 20 10-20 10z" fill="#DCE6F2" />
    <path d="M6 24v7l20 10v-7z" fill="#B4C3D7" />
    <path d="M46 24v7L26 41v-7z" fill="#A3B4CB" />
    <path
      d="M12 25l26-13"
      stroke="#E5484D"
      strokeWidth="2.5"
      strokeLinecap="round"
    />
    <circle
      cx="12"
      cy="25"
      r="2.5"
      fill="#fff"
      stroke="#E5484D"
      strokeWidth="2"
    />
    <circle
      cx="38"
      cy="12"
      r="2.5"
      fill="#fff"
      stroke="#E5484D"
      strokeWidth="2"
    />
    <path
      d="M44 42l5-9 5 5 6-10"
      fill="none"
      stroke="#1D74FF"
      strokeWidth="2.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </>,
  <>
    <path d="M10 36l22-10 22 10-22 10z" fill="#2751A3" opacity=".9" />
    <path d="M10 26l22-10 22 10-22 10z" fill="#1D74FF" opacity=".85" />
    <path d="M10 16l22-10 22 10-22 10z" fill="#F0C66D" />
  </>,
  <>
    <path d="M6 30l22-10 22 10-22 10z" fill="#DCE6F2" />
    <path d="M6 30v6l22 10v-6z" fill="#B4C3D7" />
    <path d="M50 30v6L28 46v-6z" fill="#A3B4CB" />
    <path
      d="M28 30c-5-6-7-9-7-13a7 7 0 0 1 14 0c0 4-2 7-7 13z"
      fill="#1D74FF"
    />
    <circle cx="28" cy="17" r="2.6" fill="#fff" />
    <rect
      x="40"
      y="8"
      width="20"
      height="16"
      rx="3"
      fill="#fff"
      stroke="#DCE2EA"
    />
    <rect x="43" y="11" width="14" height="6" rx="1.5" fill="#BFD8F5" />
    <rect x="43" y="17" width="14" height="4" rx="1.5" fill="#D9C9A6" />
  </>,
];

const INDEX_TARGETS = ["#view", "#survey-tools", "#layers", "#site"] as const;
const ARROW_PATH = "M7.5 4.5l5.5 5.5-5.5 5.5";

interface IndexItem {
  title: string;
  text: string;
}

/** 플랫폼 기능 색인(「현장의 모든 데이터를 한 화면에서」) — 아래 네 구역으로 가는 링크 */
export function PlatformIndex() {
  const { t } = useTranslation("landing");
  const titleId = useId();
  const items = t("site.accurate.index.items", {
    returnObjects: true,
  }) as readonly IndexItem[];
  return (
    <SiteSection labelledBy={titleId}>
      <Box sx={SITE_WRAP}>
        <Box component="header" sx={INDEX_STYLES.head}>
          <Box
            component="h2"
            id={titleId}
            ref={revealRef}
            data-reveal="words"
            sx={SITE_TYPE.h2}
          >
            <RevealWords text={t("site.accurate.index.title")} />
          </Box>
        </Box>
        <Box
          component="nav"
          aria-label={t("site.accurate.index.label")}
          sx={INDEX_STYLES.grid}
        >
          {items.map((item, index) => (
            <Box
              component="a"
              key={item.title}
              href={INDEX_TARGETS[index]}
              ref={revealRef}
              data-reveal="up"
              style={{ "--i": index } as CSSProperties}
              sx={INDEX_STYLES.link}
            >
              <svg data-icon="" viewBox="0 0 64 48" aria-hidden="true">
                {INDEX_ICONS[index]}
              </svg>
              <b>
                {item.title}
                <svg
                  width="20"
                  height="20"
                  viewBox="0 0 20 20"
                  aria-hidden="true"
                >
                  <path
                    d={ARROW_PATH}
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="1.8"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </b>
              <span>{item.text}</span>
            </Box>
          ))}
        </Box>
      </Box>
    </SiteSection>
  );
}
